import { ChevronDown, ChevronRight, Tags } from 'lucide-react';
import { useState } from 'react';
import { TagFilterPicker, tagPickerHint } from '../TagFilterPicker';
import {
  UNFILTERED,
  isFiltered,
  summarise,
  tagSelectionOf,
  withTagSelection,
  type TournamentFilter,
} from '../../tournamentFilter';
import { DateRangeFilter } from './DateRangeFilter';

/**
 * Collapsed by default, because an operator spends far more time reading the table than
 * changing what it shows — and the filters were costing a third of the screen to sit idle.
 *
 * What a collapsed panel must never do is hide the fact that rows are being withheld, so the
 * header always carries a summary of what is applied and the matched total. The screen opens
 * on a filter (this month, or the operator's saved one), so this is the normal state, not an
 * edge case.
 */
export function TournamentFilterBar({
  value,
  onChange,
  allTags,
  total,
  loading,
  isDefault,
  onSaveDefault,
  onClearDefault,
  onBulkTag,
}: {
  value: TournamentFilter;
  onChange: (next: TournamentFilter) => void;
  allTags: string[];
  total: number;
  loading: boolean;
  /** True when the current filter is exactly what's saved as the starting point. */
  isDefault: boolean;
  onSaveDefault: () => void;
  onClearDefault: () => void;
  /** Opens the bulk tag dialog for the matched set. */
  onBulkTag: () => void;
}) {
  const [open, setOpen] = useState(false);
  const chips = summarise(value);
  const filtered = isFiltered(value);

  return (
    <section className="mb-4 border border-ink-800/10 bg-white print:hidden">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-950"
        >
          {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          Filters
        </button>

        {/* A hidden filter must never be a silent one. */}
        {chips.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {chips.map((chip) => (
              <span
                key={chip}
                className="bg-felt-700 px-2 py-0.5 text-[11px] font-medium text-white"
              >
                {chip}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-ink-800/50">Showing every tournament</span>
        )}

        <span className="ml-auto text-xs text-ink-800/60">
          {loading ? 'Loading…' : `${total.toLocaleString()} ${total === 1 ? 'match' : 'matches'}`}
        </span>

        {/* Beside the count on purpose: the number this acts on is the number already being
            read, so the two belong together rather than in a toolbar a screen away. */}
        {total > 0 ? (
          <button
            type="button"
            onClick={onBulkTag}
            className="inline-flex items-center gap-1 border border-ink-800/15 px-2.5 py-1 text-xs font-medium text-ink-800 transition hover:bg-felt-50"
          >
            <Tags className="size-3.5" /> Tag these
          </button>
        ) : null}

        {filtered ? (
          <button
            type="button"
            onClick={() => onChange({ ...UNFILTERED, pageSize: value.pageSize })}
            className="text-xs text-ink-800/60 underline underline-offset-2 hover:text-ink-950"
          >
            Clear
          </button>
        ) : null}
      </div>

      {open ? (
        <div className="space-y-3 border-t border-ink-800/10 px-4 py-3">
          <DateRangeFilter
            value={value.date}
            onChange={(date) => onChange({ ...value, date })}
          />

          {allTags.length > 0 ? (
            <div className="border-t border-ink-800/10 pt-3">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="mr-1 text-xs text-ink-800/60">Tags</span>
                <TagFilterPicker
                  tags={allTags}
                  value={tagSelectionOf(value)}
                  onChange={(selection) => onChange(withTagSelection(value, selection))}
                />
              </div>
              <p className="mt-2 text-[11px] text-ink-800/50">
                {tagPickerHint(tagSelectionOf(value))}
              </p>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2 border-t border-ink-800/10 pt-3">
            <div className="flex items-center gap-3">
              {isDefault ? (
                <>
                  <span className="text-xs text-felt-700">Opens with this filter</span>
                  <button
                    type="button"
                    onClick={onClearDefault}
                    className="text-xs text-ink-800/60 underline underline-offset-2 hover:text-ink-950"
                  >
                    Forget
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={onSaveDefault}
                  className="border border-ink-800/15 px-3 py-1.5 text-xs font-medium text-ink-800 transition hover:bg-felt-50"
                >
                  Open with this filter next time
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
